import { useTranslation } from "react-i18next";
import { IconCarrot } from "../../../../common/icons/IconCarrot/IconCarrot";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { getLongPeriodFormat } from "../../../../common/util/period";
import { storedLocale } from "../../../Authentication/util";
import defaultContent from "../../../../content/ssiUplineReport";
import "./EarnedPaymentCard.scss";

type Props = {
  aboName: string;
  aboNumber: string;
  programStartDate: string; // YYYYMM
  paymentMonth: string; // YYYYMM
  sponsorName: string;
  sponsorNumber: string;
  isLoading: boolean;
  onProfileClick?: () => void;
};

export const EarnedPaymentCard = ({
  aboName,
  aboNumber,
  programStartDate,
  paymentMonth,
  sponsorName,
  sponsorNumber,
  isLoading,
  onProfileClick,
}: Props) => {
  const { t } = useTranslation(["ssiUplineReport"]);
  const locale = storedLocale();

  const formattedProgramStartDate = programStartDate ? getLongPeriodFormat(programStartDate, locale) : "";
  const formattedPaymentMonth = paymentMonth ? getLongPeriodFormat(paymentMonth, locale) : "";

  return (
    <section className="earnedPaymentCard">
      {/* Row 1 - ABO Header */}
      <div className="earnedPaymentCard__header">
        <div className="earnedPaymentCard__aboInfo">
          <LeafRectangleLoader
            isLoading={isLoading}
            width="8rem"
            height="1rem"
            margin="0"
            View={() => <div className="earnedPaymentCard__aboName">{aboName}</div>}
          />
          <LeafRectangleLoader
            isLoading={isLoading}
            width="6rem"
            height="1rem"
            margin=".5rem 0 0 0"
            View={() => <div className="earnedPaymentCard__aboNumber">#{aboNumber}</div>}
          />
        </div>
        <LeafRectangleLoader
          isLoading={isLoading}
          width="5rem"
          height="1.6rem"
          margin="0"
          View={() => (
            <button
              id={`EarnedPaymentCardProfileButton${aboNumber}`}
              className="earnedPaymentCard__profileButton"
              onClick={onProfileClick}
            >
              <span className="earnedPaymentCard__profileText">{t("profile", defaultContent.profile as string)}</span>
              <div className="earnedPaymentCard__profileUnderline" />
              <IconCarrot orientation="right" color="black" />
            </button>
          )}
        />
      </div>

      {/* Row 2 - Program Start Date */}
      <div className="earnedPaymentCard__row">
        <LeafRectangleLoader
          isLoading={isLoading}
          width="10rem"
          height="1rem"
          margin="0"
          View={() => (
            <div className="earnedPaymentCard__label">
              {t("earnedPayment.columns.programStartDate", "Program Start Date")}
            </div>
          )}
        />
        <LeafRectangleLoader
          isLoading={isLoading}
          width="6rem"
          height="1rem"
          margin="0"
          View={() => <div className="earnedPaymentCard__value">{formattedProgramStartDate}</div>}
        />
      </div>

      {/* Horizontal Rule */}
      <div className="earnedPaymentCard__divider" />

      {/* Row 3 - Payment Month */}
      <div className="earnedPaymentCard__row">
        <LeafRectangleLoader
          isLoading={isLoading}
          width="8rem"
          height="1rem"
          margin="0"
          View={() => (
            <div className="earnedPaymentCard__label">{t("earnedPayment.columns.paymentMonth", "Payment Month")}</div>
          )}
        />
        <LeafRectangleLoader
          isLoading={isLoading}
          width="6rem"
          height="1rem"
          margin="0"
          View={() => <div className="earnedPaymentCard__value">{formattedPaymentMonth}</div>}
        />
      </div>

      <div className="earnedPaymentCard__divider" />

      {/* Row 4 - Sponsor */}
      <div className="earnedPaymentCard__row earnedPaymentCard__row--noBorder">
        <LeafRectangleLoader
          isLoading={isLoading}
          width="5rem"
          height="1rem"
          margin="0"
          View={() => <div className="earnedPaymentCard__label">{t("earnedPayment.columns.sponsor", "Sponsor")}</div>}
        />
        <LeafRectangleLoader
          isLoading={isLoading}
          width="8rem"
          height="1rem"
          margin="0"
          View={() => (
            <div className="earnedPaymentCard__sponsorInfo">
              <div className="earnedPaymentCard__sponsorName">{sponsorName}</div>
            </div>
          )}
        />
      </div>
    </section>
  );
};
